import { DestroyRef, inject, Injectable } from '@angular/core';
import { EntityService } from '@bo-client/shared/dynamic-search-serverside/entity-service';
import { combineLatest, Observable, switchMap } from 'rxjs';
import { HttpHapService } from '@bo-client/core/http/http-hap.service';
import { CommissioningRequestDtoModel } from '@bo-client/activation-management/shared/models/commissioningRequestDto';
import { path } from '@bo-client/shared/constants/path';
import { Router } from '@angular/router';
import { AuthorizationService } from '@bo-client/core/authorization/authorization.service';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatDialogSize } from '@bo-client/shared/constants/mat-dialog-size';
import { GenericTwoOptionsModalComponent } from '@bo-client/shared/modals/generic-two-options-modal/generic-two-options-modal.component';
import { uri } from '@bo-client/shared/constants/uri';
import { ToastrService } from 'ngx-toastr';
import { AreaTypesModel } from '@bo-client/shared/models/areaTypes';
import {
  ExpiredPassModel,
  ValidAndExpiredPassModel,
  ValidPassModel
} from '@bo-client/activation-management/shared/models/validPassWithUsersModel';
import { AdvancedFilterModel, ColumnState } from 'ag-grid-community';
import { InstallerPassModel } from '@bo-client/shared/models/installerPassDto';
import { UserDto } from '@shared/models';
import { DataShareService } from '@bo-client/shared/services/data-share.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { combineLatestWith, map, shareReplay } from 'rxjs/operators';
import { RowActionModel } from '@bo-client/shared/entity-list-page/cell-renderers/actions-cell-renderer/RowActionModel';
import {
  ActionTypeEnum,
  AreaTypeEnum,
  CommissioningPowerMeterType,
  CommonStatusEnum
} from '@bo-client/activation-management/shared/models/commissioningModel';
import { InstallerPassStatusEnum } from '@bo-client/technician-management/tm-user/tm-user.model';
import { CreatableAction } from '@bo-client/shared/entity-list-page/entity-list-page.component';
import { ValidPassesModalComponent } from '@bo-client/activation-management/commissioning/commissioning-request-list-v2/valid-passes-modal/valid-passes-modal.component';
import { GenericConfirmationModalModel } from '@bo-client/shared/models/genericConfirmationModal.model';
import { MetaService } from '@bo-client/core/api/meta-service';

@Injectable()
export class CommissioningRequestListV2Service implements EntityService {
  private httpHapClient = inject(HttpHapService);
  private router = inject(Router);
  private authorizationService = inject(AuthorizationService);
  private toastService = inject(ToastrService);
  private matDialog = inject(MatDialog);
  private dataShareService = inject(DataShareService);
  private destroyRef = inject(DestroyRef);
  private metaService = inject(MetaService);

  baseUrl = '/commissioningrequest/filter/v2';

  currentUser$: Observable<UserDto>;
  passes$: Observable<ValidAndExpiredPassModel>;
  areaTypes$: Observable<AreaTypesModel[]>;

  constructor() {
    this.currentUser$ = this.dataShareService.userData$.pipe(
      takeUntilDestroyed(this.destroyRef),
      shareReplay(1)
    );

    this.areaTypes$ = this.httpHapClient.getTyped<AreaTypesModel[]>(uri.areaTypes);

    this.passes$ = this.currentUser$.pipe(
      switchMap((user) => {
        const isTechnicianAdmin = !!user.areaTypes?.length;
        const installerPassObservable = isTechnicianAdmin
          ? this.httpHapClient.getTyped<InstallerPassModel[]>(uri.tmGetAllPassesByOwnBusinessPartner)
          : this.httpHapClient.getTyped<InstallerPassModel[]>(uri.tmGetAllPassesByuser + '/' + user.id);
        return installerPassObservable.pipe(
          map((passes) =>
            passes.filter((pass) => {
              return (
                pass.user.id === user.id || user.areaTypes.some((areaType) => areaType.id === pass.idarea)
              );
            })
          )
        );
      }),
      combineLatestWith(this.dataShareService.tenantConfig$),
      map(([passes, config]) => {
        const blacklistedAreaIds: number[] = [];
        if (config?.commissioning?.gasConfig?.deactivateProcess) {
          blacklistedAreaIds.push(1); // ID für Gas
        }
        if (config?.commissioning?.powerConfig?.deactivateProcess) {
          blacklistedAreaIds.push(2); // ID für Strom
        }
        if (config?.commissioning?.waterConfig?.deactivateProcess) {
          blacklistedAreaIds.push(5); // ID für Wasser
        }
        return passes.filter((pass) => !blacklistedAreaIds.includes(pass.idarea));
      }),
      combineLatestWith(this.areaTypes$),
      map(([passes, areaTypes]) => this.checkExpiredPasses(areaTypes, passes))
    );
  }

  loadData(
    tenant: string,
    environment: string,
    page: number,
    size: number,
    filterModel: AdvancedFilterModel,
    columnState: ColumnState[]
  ): Observable<{
    content: CommissioningRequestDtoModel[];
    totalElements: number;
  }> {
    const result = columnState
      .filter((item) => item.sort)
      .map((item) => ({ colId: item.colId, sort: item.sort }));

    const filterAndSortModel = {
      filterModel: filterModel,
      sortModel: result
    };

    return this.httpHapClient.postTyped<{
      content: CommissioningRequestDtoModel[];
      totalElements: number;
    }>(filterAndSortModel, `${this.baseUrl}?page=${page}&size=${size}`);
  }

  openNewCommissioningRequest(areaType: AreaTypesModel, passes: ValidPassModel[]) {
    const availablePasses = passes.filter((pass) => pass.areaId === areaType.id);
    if (!availablePasses.length) return;
    if (availablePasses.length === 1) {
      this.navigateToCreatePage({ areaType: areaType.name as AreaTypeEnum, passId: availablePasses[0].id });
      return;
    }
    this.openValidPassesModal(areaType, passes);
  }

  navigateToCreatePage(params: { areaType: AreaTypeEnum; passId: number }) {
    this.router.navigate(
      [
        `${path.cmPrefix}/${path.cmCommissioningManagement}/${path.create}/${params.areaType
          .toString()
          .toLowerCase()}/${params.passId}`
      ],
      {
        replaceUrl: false
      }
    );
  }

  getEditUrl(id: number | string): string {
    return `${path.cmPrefix}/${path.cmCommissioningManagement}${path.edit}/${id}`;
  }

  navigateToEditPage(id: number | string) {
    this.router.navigate([this.getEditUrl(id)], {
      replaceUrl: false
    });
  }

  deleteCommissioningRequest(id: string, onSuccess: () => void) {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.id = 'commissioning-request-into-list-delete';
    dialogConfig.maxHeight = MatDialogSize.maxHeight;
    dialogConfig.width = MatDialogSize.width;
    dialogConfig.data = {
      title: 'Inbetriebnahme löschen?',
      message: 'Sind Sie sicher, dass Sie die Inbetriebnahme löschen möchten?',
      optionA: 'Abbrechen',
      optionB: 'Löschen bestätigen'
    };
    this.matDialog
      .open(GenericTwoOptionsModalComponent, dialogConfig)
      .afterClosed()
      .pipe()
      .subscribe({
        next: (result) => {
          if (result) {
            if (result == 'optionB') {
              this.deleteRegister(id, onSuccess);
            }
          }
        },
        error: () => {
          this.toastService.error('Beim Löschen ist ein Fehler aufgetreten');
        }
      });
  }

  getRowActions$ = (): Observable<RowActionModel<CommissioningRequestDtoModel>[]> => {
    const permissions$ = combineLatest({
      read: this.authorizationService.hasPermission$('COMMISSIONING_READ'),
      write: this.authorizationService.hasPermission$('COMMISSIONING_UPDATE'),
      delete: this.authorizationService.hasPermission$('COMMISSIONING_DELETE')
    });
    const isKeyUser$ = this.authorizationService.hasRole$('ROLE_KEYUSER_COMMISSIONING');
    const isInstaller$ = this.authorizationService.isInstaller$;

    return combineLatest([isKeyUser$, isInstaller$, permissions$, this.dataShareService.tenantConfig$]).pipe(
      takeUntilDestroyed(this.destroyRef),
      map(([isKeyUser, isInstaller, permissions, tenantConfig]) => [
        ...(permissions.read && !permissions.write
          ? [
              {
                icon: 'search',
                tooltip: 'Ansehen',
                action: (commissioningRequest) => this.navigateToEditPage(commissioningRequest.id)
              }
            ]
          : []),
        ...(permissions.write
          ? [
              {
                icon: 'edit',
                tooltip: 'Bearbeiten',
                action: (commissioningRequest) => this.navigateToEditPage(commissioningRequest.id)
              }
            ]
          : []),
        ...(permissions.delete && (isInstaller || isKeyUser)
          ? [
              {
                icon: 'delete',
                iconColor: 'warn',
                tooltip: 'Löschen',
                action: (commissioningRequest, onSuccess) =>
                  this.deleteCommissioningRequest(commissioningRequest.id.toString(), onSuccess),
                isVisible: (commissioningRequest) =>
                  isKeyUser ||
                  (isInstaller &&
                    !tenantConfig?.common?.disableInstallerDeletion &&
                    commissioningRequest.state === CommonStatusEnum.Incomplete)
              } as RowActionModel<CommissioningRequestDtoModel>
            ]
          : [])
      ])
    );
  };

  getCreatableActions$(): Observable<CreatableAction[]> {
    return this.areaTypes$.pipe(
      takeUntilDestroyed(this.destroyRef),
      combineLatestWith(this.passes$),
      map(([areaTypes, passes]) => {
        const creatableActions: CreatableAction[] = [];
        const validPassCreatables = this.mapValidPassListCreatables(passes.validPassList, areaTypes);
        creatableActions.push(...validPassCreatables);

        const expiredPassCreatables = this.mapExpiredPassListCreatables(passes.expiredPassList, areaTypes);
        creatableActions.push(...expiredPassCreatables);

        return creatableActions;
      })
    );
  }

  private mapValidPassListCreatables(
    passes: ValidPassModel[],
    areaTypes: AreaTypesModel[]
  ): CreatableAction[] {
    const areasWithPasses: [AreaTypesModel, ValidPassModel[]][] = areaTypes.map((areaType) => [
      areaType,
      passes.filter((pass) => pass.areaId === areaType.id)
    ]);
    return areasWithPasses
      .filter((tuple) => !!tuple[1].length)
      .map(
        (tuple) =>
          ({
            title: tuple[0].description,
            action: () => this.openNewCommissioningRequest(tuple[0], tuple[1]),
            color: 'primary',
            description: 'Legen Sie eine neue Inbetriebnahme mit einem gültigen Ausweis an',
            iconName: 'add'
          } as CreatableAction)
      );
  }

  private mapExpiredPassListCreatables(
    passes: ExpiredPassModel[],
    areaTypes: AreaTypesModel[]
  ): CreatableAction[] {
    return passes.map((expiredPass) => ({
      title: expiredPass.description,
      action: () =>
        this.openExpiredPassModal(areaTypes.find((area) => area.id === expiredPass.areaId)!, expiredPass),
      iconName: 'edit',
      color: 'warn'
    }));
  }

  private getCorrectPassesForArea(
    areaTypeId: number,
    listPasses: InstallerPassModel[]
  ): InstallerPassModel[] {
    return listPasses.filter(
      (pass) =>
        pass.idarea === areaTypeId &&
        (pass.status === InstallerPassStatusEnum.Valid || pass.status === InstallerPassStatusEnum.Expired)
    );
  }

  private createExpiredPassItem(pass: InstallerPassModel, areaType: AreaTypesModel): ExpiredPassModel {
    return {
      id: pass.id,
      passNumber: pass.passNummer,
      userName: `${pass.user.contactData?.firstName || ''} ${pass.user.contactData?.lastName || ''}`.trim(),
      companyLocationName: pass.companylocation?.name || pass.companylocation?.external_id,
      areaId: areaType.id,
      description: `${areaType.description}: ${pass.passNummer} abgelaufen`
    };
  }

  private addValidPassToList(
    pass: InstallerPassModel,
    validPassList: ValidPassModel[],
    areaType: AreaTypesModel
  ): void {
    if (!validPassList.some((item) => item.id === areaType.id)) {
      validPassList.push({
        id: pass.id,
        passNumber: pass.passNummer,
        userName: `${pass.user.contactData?.firstName || ''} ${pass.user.contactData?.lastName || ''}`.trim(),
        companyLocationName: pass.companylocation?.name || pass.companylocation?.external_id,
        areaId: areaType.id,
        name: areaType.name,
        description: areaType.description
      });
    }
  }

  private checkExpiredPasses(
    areaTypeList: AreaTypesModel[],
    listPasses: InstallerPassModel[]
  ): ValidAndExpiredPassModel {
    const validPassList: ValidPassModel[] = [];
    const expiredPassList: ExpiredPassModel[] = [];

    areaTypeList.forEach((areaType) => {
      const passesInArea = this.getCorrectPassesForArea(areaType.id, listPasses);
      passesInArea.forEach((pass) => {
        if (
          pass.status === InstallerPassStatusEnum.Expired ||
          (pass.expirationdate && new Date(pass.expirationdate).getTime() < Date.now())
        ) {
          expiredPassList.push(this.createExpiredPassItem(pass, areaType));
        } else {
          this.addValidPassToList(pass, validPassList, areaType);
        }
      });
    });
    return {
      validPassList,
      expiredPassList
    };
  }

  private deleteRegister(id: string, onSuccess: () => void) {
    this.httpHapClient
      .delete<void>(uri.cmCommissioningRequest + '/' + id)
      .pipe()
      .subscribe({
        next: () => {
          this.toastService.success('Anfrage für Inbetriebnahme erfolgreich gelöscht.');
          onSuccess();
        },
        error: () => {
          this.toastService.error('Beim Löschen ist ein Fehler aufgetreten');
        }
      });
  }

  private openValidPassesModal(areaType: AreaTypesModel, validPasses: ValidPassModel[]) {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.id = 'valid-pass-list';
    dialogConfig.maxHeight = '70%';
    dialogConfig.width = '70%';
    dialogConfig.data = {
      areaType: areaType,
      validPasses: validPasses
    };
    this.matDialog
      .open(ValidPassesModalComponent, dialogConfig)
      .afterClosed()
      .subscribe((pass: ValidPassModel | null) => {
        if (pass) {
          this.navigateToCreatePage({ areaType: areaType.name as AreaTypeEnum, passId: pass.id });
        }
      });
  }

  private openExpiredPassModal(areaType: AreaTypesModel, expiredPass: ExpiredPassModel) {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.id = 'expired-pass-list';
    dialogConfig.maxHeight = '70%';
    dialogConfig.width = '70%';
    dialogConfig.data = {
      title: `Ausweis mit Nummer ${expiredPass.passNumber} ist abgelaufen`,
      message: `Der Ausweis für die Sparte ${areaType.description} im Betriebsbereich ${expiredPass.companyLocationName} ist abgelaufen. Möchten Sie für diesen Ausweis eine Verlängerung beantragen?`,
      optionA: 'Abbrechen',
      optionB: 'Verlängerung beantragen'
    } as GenericConfirmationModalModel;
    this.matDialog
      .open(GenericTwoOptionsModalComponent, dialogConfig)
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (result && result === 'optionB') {
          this.router.navigate([path.tmPath + path.tmInstallerPassEdit + '/' + expiredPass.id]);
        }
      });
  }

  getActionFilterOptions$() {
    return this.dataShareService
      .getSingleTenantConfig$<CommissioningPowerMeterType[]>(
        'commissioning.powerConfig.availablePowerMeterTypes'
      )
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        map((config) => {
          return this.metaService.getActionFilterOptions().filter((option) => {
            return (
              Object.values(ActionTypeEnum).some(
                (actionType) => actionType === (option.value as ActionTypeEnum | CommissioningPowerMeterType)
              ) ||
              config.some(
                (meterType) => meterType === (option.value as ActionTypeEnum | CommissioningPowerMeterType)
              )
            );
          });
        })
      );
  }
}
